import * as THREE from 'three'
import TWEEN from '@tweenjs/tween.js'
import Scene from './Scene'
import Model from '@/objects/Model'
import config from '@/config/hospital'
import { inRange } from '@/utils/math'
import store, { watch } from '@/store'

const clock = new THREE.Clock(false)

export default class HospitalScene extends Scene {
  name = 'HospitalScene'

  constructor (stage) {
    super()

    this.stage = stage
    this.paused = true
    this.tween = new TWEEN.Group()

    this.createEnvironment()
    this.createCharacter()
  }

  createEnvironment () {
    const { path, rotation, position } = config.environment

    this.entrance = {
      isOpen: false,
      doors: []
    }

    this.environment = new Model(path, () => {
      config.entrance.objects.forEach(({ name, open, closed }) => {
        const door = this.environment.getObjectByName(name)
        this.entrance.doors.push({ ...door, open, closed })
      })
    })

    this.environment.rotateY(rotation)
    this.environment.position.copy(position)
  }

  createCharacter () {
    const { path, timeScale, rotation, position } = config.character
    this.character = new Model(path, () => {
      this.character.setShadow(3, config.scene.shadow)
      this.character.setTimeScale(timeScale)
      this.character.rotateY(rotation)
      this.character.position.copy(position)
    })
  }

  createPlayer () {
    if (this.player) return

    const { timeScale, rotation, position } = config.player
    this.player = new Model(store.getters.playerModel, () => {
      this.player.setShadow(3, config.scene.shadow)
      this.player.setTimeScale(timeScale)
      this.player.rotateY(rotation)
      this.player.position.copy(position)
    })
  }

  openDoors (duration = 2000) {
    this.entrance.isOpen = true
    this.tween.removeAll()
    this.entrance.doors.forEach(door => {
      new TWEEN.Tween(door.position, this.tween)
        .to(door.open, duration)
        .start()
    })
  }

  closeDoors (duration = 1000) {
    this.entrance.isOpen = false
    this.tween.removeAll()
    this.entrance.doors.forEach(door => {
      new TWEEN.Tween(door.position, this.tween)
        .to(door.closed, duration)
        .start()
    })
  }

  watchStore () {
    this.unwatch = watch('data', data => {
      if (data) {
        const { index, fragments } = data
        const { position, camera: { x, y, z } } = fragments[index]
        this.environment.position.x = -position
        this.stage.changeCameraPosition(x, y, z)
      }
    }, { immediate: true })
  }

  start () {
    const { background, fog } = config.scene

    this.createPlayer()

    this.stage.add(this.environment)
    this.stage.add(this.character)
    this.stage.add(this.player)

    this.stage.changeBackground(background)
    this.stage.changeFogColor(fog.color)
    this.stage.changeFogDistance(fog.near, fog.far)

    this.watchStore()
    this.resume()
  }

  stop () {
    this.pause()
    this.unwatch()

    this.stage.remove(this.environment)
    this.stage.remove(this.character)
    this.stage.remove(this.player)

    this.environment.position.x = 0
    this.entrance.doors.forEach(door => {
      door.position.copy(door.closed)
    })
  }

  pause () {
    clock.stop()
    this.paused = true
  }

  resume () {
    clock.start()
    this.paused = false
  }

  update () {
    if (this.paused) return

    const delta = clock.getDelta()

    this.environment.position.x -= delta * config.speed
    this.character.update(delta)
    this.player.update(delta)

    const { isOpen } = this.entrance
    const { open, close } = config.entrance
    const position = Math.abs(this.environment.position.x)

    if (inRange(position, open, close)) {
      !isOpen && this.openDoors()
    } else {
      isOpen && this.closeDoors()
    }

    this.tween.update()
  }
}
